import {
    Menu as HeadlessUiMenu,
    MenuButton as HeadlessUiMenuButton,
    MenuItems as HeadlessUiMenuItems,
    MenuSeparator,
    MenuItem as HeadlessUiMenuItem,
    Transition,
} from "@headlessui/react";
import ChevronRightIcon from "@vesta/aergia/svgs/core/chevron-right-2.svg?react";
import ChevronDownIcon from "@vesta/aergia/svgs/core/chevron-down-2.svg?react";
import type {ReactNode} from "react";
import {isPrimitiveType, tw} from "@vesta/aergia/utilities/utilities";
import {twJoin} from "tailwind-merge";
import {Spinner} from "@vesta/aergia/components/spinner";
import React, {useEffect, useRef, useState} from "react";
import {mapPlacementToAnchor, type DropdownItemProps, type DropdownMenuItemPlacement} from "@vesta/aergia/utilities/types";
import {Button} from "@vesta/aergia/components/button";

export function Dropdown({
    appearance,
    children,
    isLoading,
    shouldFitContainer,
    trigger,
    // isOpen,
    //onOpenChange,
    placement,
    testId,
    label,
    className,
    bottomSection,
}: {
    appearance?: "default" | "tall";
    children: React.ReactElement<DropdownItemProps, typeof DropdownItem> | React.ReactElement<DropdownItemProps, typeof DropdownItem>[];
    isLoading?: boolean;
    //autoFocus?: boolean;
    shouldFitContainer?: boolean;
    trigger: string;
    // isOpen?: boolean;
    //onOpenChange?: (args: {isOpen: boolean}) => void;
    placement?: DropdownMenuItemPlacement;
    testId?: string;
    label?: string;
    className?: string;
    bottomSection?: React.ReactNode;
}) {
    // React.Children.forEach(children, (child) => {
    //     if (!React.isValidElement(child)) return;
    //     if (child.type !== DropdownItem) {
    //         throw new Error("To Do");
    //     }
    // });

    const backgroundClassName = tw`bg-ds-background-neutral`;
    const textClassName = tw`text-ds-text`;
    const cursorClassName = null;
    const hoverBackgroundClassName = tw`hover:bg-ds-background-neutral-hovered`;
    const hoverTextClassName = tw`hover:text-ds-text`;
    const activeBackgroundClassName = tw`data-active:bg-ds-background-selected data-active:hover:bg-ds-background-selected-hovered`;
    const activeTextClassName = tw`data-active:text-ds-text-selected `;

    const borderClassName = tw`border-none focus:outline-none`;
    const pxClassName = tw`pl-ds-space-150 pr-ds-space-100`;
    const pyClassName = tw`py-ds-space-075`;

    const widthClassName = shouldFitContainer === true ? tw`w-full mx-auto` : tw`max-w-full`;

    const heightClassName = tw`h-[2rem]`;
    const menuHeightClassName = appearance === "tall" ? tw`max-h-[100vh]` : tw`max-h-[20rem]`;
    const spinnerSizeClassName = tw`w-6 h-6`;

    const menuItemPlacement = appearance === "tall" ? "right start" : mapPlacementToAnchor(placement);
    const heightMenuItemClassName = isLoading === true ? tw`h-[56px]` : "tw-h-fit";
    const widthMenuItemClassName = isLoading === true ? tw`min-w-[160px]` : shouldFitContainer === true ? tw`w-(--button-width) mx-auto` : tw`max-w-full`;
    const pxMenuItemClassName = isLoading === true ? tw`px-ds-space-250` : "";
    const pyMenuItemClassName = isLoading === true ? tw`py-ds-space-250` : "";
    const backgroundMenuItemClassName = tw`bg-ds-surface-overlay`;
    const textMenuItemClassName = tw`text-ds-text`;

    const showChevronIcon = shouldFitContainer === true ? false : isPrimitiveType(trigger);

    return (
        <HeadlessUiMenu
            as="div"
            className="relative"
            data-testid={testId}
            aria-label={label}
        >
            <HeadlessUiMenuButton
                className={twJoin(
                    widthClassName,
                    heightClassName,
                    pxClassName,
                    pyClassName,
                    backgroundClassName,
                    textClassName,
                    cursorClassName,
                    hoverBackgroundClassName,
                    activeBackgroundClassName,
                    borderClassName,
                    hoverTextClassName,
                    activeTextClassName,
                    menuHeightClassName,
                    "inline-flex gap-ds-space-050 justify-center items-center",
                    "relative",
                    "rounded-ds-border-radius-100",
                    "text-ds-font-body",
                    "!font-ds-font-weight-medium",
                    "transition-colors duration-small",
                )}
            >
                {trigger}
                {showChevronIcon && <ChevronDownIcon className="fill-current" />}
            </HeadlessUiMenuButton>

            <HeadlessUiMenuItems
                transition
                // anchor={{to: menuItemPlacement, gap: "8px"}}
                className={twJoin(
                    heightMenuItemClassName,
                    widthMenuItemClassName,
                    pxMenuItemClassName,
                    pyMenuItemClassName,
                    backgroundMenuItemClassName,
                    textMenuItemClassName,
                    "absolute z-[400] block shadow-ds-shadow-overlay focus:outline-none",
                    "rounded-ds-border-radius",
                    "transition duration-small",
                    "mt-ds-space-100",
                    className,
                )}
                modal={false}
            >
                {/* <MenuSeparator className="my-1 h-[6px]" /> */}
                <div className={twJoin("transition-opacity duration-medium", isLoading === true ? "opacity-0" : "opacity-100")}>{children}</div>
                {/* <MenuSeparator className="my-1 h-[6px]" /> */}

                {bottomSection}

                <Transition show={isLoading === true}>
                    <div
                        className={twJoin(
                            "absolute inset-0",
                            "grid place-items-center",
                            "transition-opacity duration-medium",
                            "opacity-100 data-closed:opacity-0",
                        )}
                    >
                        <Spinner className={spinnerSizeClassName} />
                    </div>
                </Transition>
            </HeadlessUiMenuItems>
        </HeadlessUiMenu>
    );
}

export function DropdownItem({children, spacing, autoFocus}: DropdownItemProps) {
    const backgroundClassName = tw`bg-ds-background-neutral-subtle`;
    const textClassName = tw`text-ds-font-body `;
    const hoverBackgroundClassName = tw`hover:bg-ds-background-neutral-subtle-hovered`;
    const pyClassName = spacing === "compact" ? "py-ds-space-050" : "py-ds-space-100";
    const pxClassName = spacing === "compact" ? "px-ds-space-150" : "px-ds-space-200";
    const mxClassName = tw`mx-ds-space-0`;
    const myClassName = tw`my-ds-space-0`;
    const heightClassName = tw`min-h-[40px]`;
    const focusOutlineClassName =
        autoFocus === true
            ? tw`focus-visible:outline-ds-border-width-outline focus-visible:outline-solid focus-visible:outline-ds-border-focused focus-visible:outline-offset-[calc(0px-var(--ds-border-width-outline))]`
            : "";

    return (
        <HeadlessUiMenuItem>
            <button
                type="button"
                className={twJoin(
                    backgroundClassName,
                    textClassName,
                    hoverBackgroundClassName,
                    pxClassName,
                    pyClassName,
                    mxClassName,
                    myClassName,
                    heightClassName,
                    focusOutlineClassName,
                    "flex items-center",
                    "w-full",
                    "relative",
                )}
            >
                {children}
            </button>
        </HeadlessUiMenuItem>
    );
}

export function NestedDropdownItem({label, children}: any) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close when clicking outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div
            ref={ref}
            className="relative"
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                }}
                className={twJoin(
                    "flex w-full items-center justify-between px-ds-space-200 py-ds-space-100 cursor-pointer",
                    "hover:bg-ds-background-neutral-subtle-hovered",
                )}
            >
                <span>{label}</span>
                <ChevronRightIcon className="ml-1 h-4 w-4 shrink-0" />
            </div>

            {open && (
                <div
                    className={twJoin(
                        "absolute left-full top-0 ml-1 min-w-[10rem] bg-ds-surface-overlay rounded-ds-border-radius shadow-ds-shadow-overlay z-[450]",
                    )}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export type OnOpenChangeArgs = {
    isOpen: boolean;
    event: React.SyntheticEvent | null;
};

export function MenuCustomDropdown({
    trigger,
    children,
    placement = "bottom-start",
    isLoading,
    shouldFitContainer,
    isSelectedExternal,
    isOpen,
    onOpenChange = () => {},
    closeRef,
}: {
    trigger: React.ReactElement<{isSelected?: boolean}>;
    children: React.ReactElement;
    placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
    isLoading?: boolean;
    shouldFitContainer?: boolean;
    isSelectedExternal?: boolean;
    isOpen?: boolean;
    onOpenChange?: (args: OnOpenChangeArgs) => void;
    closeRef?: React.RefObject<(() => void) | null>;
}) {
    const textClassName = tw`text-ds-text`;
    const widthClassName = shouldFitContainer === true ? tw`w-full mx-auto` : tw`max-w-full`;

    const menuItemPlacement = mapPlacementToAnchor(placement);
    const heightMenuItemClassName = isLoading === true ? tw`h-[56px]` : "tw-h-fit";
    const widthMenuItemClassName = isLoading === true ? tw`min-w-[160px]` : shouldFitContainer === true ? tw`w-(--button-width) mx-auto` : tw`max-w-full`;
    const pxMenuItemClassName = isLoading === true ? tw`px-ds-space-250` : "";
    const pyMenuItemClassName = isLoading === true ? tw`py-ds-space-250` : "";
    const backgroundMenuItemClassName = tw`bg-ds-surface-overlay`;
    const textMenuItemClassName = tw`text-ds-text`;

    const closeCallbackRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (isOpen === false && closeCallbackRef.current) {
            closeCallbackRef.current();
        }
    }, [isOpen]);

    return (
        <HeadlessUiMenu
            as="div"
            className={twJoin("relative flex text-left")}
        >
            {({open, close}) => {
                closeCallbackRef.current = close;

                if (closeRef) {
                    closeRef.current = close;
                }

                // Use isOpen prop if provided, otherwise fall back to internal open state
                const isControlled = isOpen !== undefined;
                const effectiveOpen = isControlled ? isOpen : open;
                const mergedSelected = effectiveOpen || isSelectedExternal;

                React.useEffect(() => {
                    // Call onOpenChange whenever the internal open state changes
                    onOpenChange({isOpen: open, event: null});
                }, [open]);

                return (
                    <>
                        <HeadlessUiMenuButton
                            as="div"
                            className={twJoin(
                                widthClassName,
                                textClassName,
                                "inline-flex gap-ds-space-050 justify-center items-center",
                                "relative",
                                "rounded-ds-border-radius-100",
                                "text-ds-font-body",
                                "!font-ds-font-weight-medium",
                                "transition-colors duration-small",
                            )}
                            disabled={isControlled}
                        >
                            {React.cloneElement(trigger, {isSelected: mergedSelected})}
                        </HeadlessUiMenuButton>

                        {effectiveOpen && (
                            <div className={twJoin("line-clamp-1", "transition-opacity duration-medium", isLoading === true ? "opacity-0" : "opacity-100")}>
                                <HeadlessUiMenuItems
                                    static
                                    anchor={{to: menuItemPlacement, gap: "8px"}}
                                    modal={false}
                                    className={twJoin(
                                        heightMenuItemClassName,
                                        widthMenuItemClassName,
                                        pxMenuItemClassName,
                                        pyMenuItemClassName,
                                        backgroundMenuItemClassName,
                                        textMenuItemClassName,
                                        "absolute z-[200] block shadow-ds-shadow-overlay focus:outline-none",
                                        "rounded-ds-border-radius",
                                        "transition duration-small",
                                        "!overflow-hidden",
                                    )}
                                >
                                    {children}
                                </HeadlessUiMenuItems>
                            </div>
                        )}
                    </>
                );
            }}
        </HeadlessUiMenu>
    );
}
